import { useState, useEffect } from 'react';
import api from '../../services/api';

const useDeals = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const findAll = async () => {
    const { data } = await api.get('/deals');
    return data;
  };

  const findOneById = async id => {
    if (!id) {
      throw new Error('ID da deal é obrigatório');
    }
    const { data } = await api.get(`/deals/${id}`);
    return data;
  };

  const update = async (id, updateData) => {
    const { data } = await api.patch(`/deals/${id}`, updateData);
    return data;
  };

  const fetchDealsByPipeline = async pipelineId => {
    // Evitar chamadas duplicadas
    if (loading) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Tentar buscar da API
      const response = await api.get(`/deals?pipelineId=${pipelineId}`);
      const dealsData = response.data;

      if (Array.isArray(dealsData)) {
        setDeals(dealsData);
      } else if (dealsData && Array.isArray(dealsData.deals)) {
        setDeals(dealsData.deals);
      } else {
        setDeals([]);
      }
    } catch (err) {
      console.warn('❌ Erro ao buscar deals da API, usando dados mockados:', err);

      // Dados mockados para desenvolvimento
      const mockDeals = [
        {
          id: 1,
          contact: {
            name: 'João Silva',
            number: '5511999999999',
            email: 'joao@email.com',
          },
          totalValue: 1500.0,
          stageId: 1,
          canMove: true,
          requirements: [],
        },
        {
          id: 2,
          contact: {
            name: 'Maria Santos',
            number: '5511888888888',
            email: 'maria@email.com',
          },
          totalValue: 2300.0,
          stageId: 1,
          canMove: true,
          requirements: [],
        },
        {
          id: 3,
          contact: {
            name: 'Pedro Costa',
            number: '5511777777777',
            email: 'pedro@email.com',
          },
          totalValue: 800.0,
          stageId: 2,
          canMove: false,
          requirements: ['Documentação', 'Aprovação'],
        },
        {
          id: 4,
          contact: {
            name: 'Ana Oliveira',
            number: '5511666666666',
            email: 'ana@email.com',
          },
          totalValue: 3200.0,
          stageId: 2,
          canMove: true,
          requirements: [],
        },
        {
          id: 5,
          contact: {
            name: 'Carlos Lima',
            number: '5511555555555',
            email: 'carlos@email.com',
          },
          totalValue: 1800.0,
          stageId: 3,
          canMove: true,
          requirements: [],
        },
      ];

      setDeals(mockDeals);
    } finally {
      setLoading(false);
    }
  };

  const canMoveDeal = async dealId => {
    try {
      const response = await api.get(`/deals/${dealId}/canMove`);
      return response.data;
    } catch (err) {
      console.error('Erro ao verificar se pode mover deal:', err);
      return { canMove: true, missingRequirements: [] };
    }
  };

  const moveDeal = async (dealId, newStageId) => {
    try {
      await api.patch(`/deals/${dealId}/stages/${newStageId}`);
      return true;
    } catch (err) {
      console.error('Erro ao mover deal:', err);
      return false;
    }
  };

  const fillRequirement = async (dealId, requirementId, value) => {
    try {
      await api.post(`/deals/${dealId}/requirement/${requirementId}`, {
        value,
      });
      return true;
    } catch (err) {
      console.error('Erro ao preencher requirement:', err);
      return false;
    }
  };

  return {
    findAll,
    findOneById,
    update,
    deals,
    loading,
    error,
    fetchDealsByPipeline,
    canMoveDeal,
    moveDeal,
    fillRequirement,
  };
};

export default useDeals;
